/**
 * File Preview Component
 *
 * Preview for PDF, Images, and other document types
 */

'use client';

import { useState } from 'react';
import { type DesignFile, getFileCategory } from './types';
import { Skeleton } from '@/components/base/Skeleton';

interface FilePreviewProps {
  file: DesignFile;
}

export default function FilePreview({ file }: FilePreviewProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(100);

  // Use file.type if explicitly set, otherwise detect from filename
  const category = (file.type && ['BIM', 'CAD', 'GIS', 'PDF', 'IMAGE', 'OTHER'].includes(file.type.toUpperCase()))
    ? file.type.toUpperCase()
    : getFileCategory(file.name);
  const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

  const handleLoad = () => {
    setLoading(false);
  };

  const handleError = () => {
    setLoading(false);
    setError('Failed to load file');
  };

  // PDF Preview
  if (category === 'PDF') {
    return (
      <div className="w-full h-full flex flex-col bg-gray-100">
        {/* Toolbar */}
        <div className="bg-white border-b px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <i className="ri-file-pdf-line text-red-500"></i>
            <span className="text-sm font-medium truncate max-w-[200px]">{file.name}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setZoom(Math.max(25, zoom - 25))}
              className="p-1.5 hover:bg-gray-100 rounded"
              title="Zoom Out"
            >
              <i className="ri-zoom-out-line"></i>
            </button>
            <span className="text-sm text-gray-600 min-w-[50px] text-center">{zoom}%</span>
            <button
              onClick={() => setZoom(Math.min(200, zoom + 25))}
              className="p-1.5 hover:bg-gray-100 rounded"
              title="Zoom In"
            >
              <i className="ri-zoom-in-line"></i>
            </button>
            <div className="w-px h-6 bg-gray-200 mx-2"></div>
            <a
              href={file.url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 hover:bg-gray-100 rounded"
              title="Open in new tab"
            >
              <i className="ri-external-link-line"></i>
            </a>
            <a
              href={file.url}
              download={file.name}
              className="p-1.5 hover:bg-gray-100 rounded"
              title="Download"
            >
              <i className="ri-download-line"></i>
            </a>
          </div>
        </div>

        {/* PDF Viewer */}
        <div className="flex-1 overflow-auto p-4">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
              <Skeleton shape="rounded" className="h-full w-full" />
            </div>
          )}
          <iframe
            src={`${file.url}#zoom=${zoom}`}
            className="w-full h-full border-0 bg-white shadow-lg"
            style={{ minHeight: '500px' }}
            onLoad={handleLoad}
            onError={handleError}
          />
        </div>
      </div>
    );
  }

  // Image Preview
  if (category === 'IMAGE') {
    return (
      <div className="w-full h-full flex flex-col bg-gray-900">
        {/* Toolbar */}
        <div className="bg-gray-800 border-b border-gray-700 px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2 text-gray-300">
            <i className="ri-image-line text-green-400"></i>
            <span className="text-sm font-medium truncate max-w-[200px]">{file.name}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setZoom(Math.max(25, zoom - 25))}
              className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded"
            >
              <i className="ri-zoom-out-line"></i>
            </button>
            <span className="text-sm text-gray-400 min-w-[50px] text-center">{zoom}%</span>
            <button
              onClick={() => setZoom(Math.min(300, zoom + 25))}
              className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded"
            >
              <i className="ri-zoom-in-line"></i>
            </button>
            <button
              onClick={() => setZoom(100)}
              className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded"
              title="Reset Zoom"
            >
              <i className="ri-refresh-line"></i>
            </button>
            <div className="w-px h-6 bg-gray-600 mx-2"></div>
            <a
              href={file.url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded"
            >
              <i className="ri-external-link-line"></i>
            </a>
            <a
              href={file.url}
              download={file.name}
              className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded"
            >
              <i className="ri-download-line"></i>
            </a>
          </div>
        </div>

        {/* Image */}
        <div className="flex-1 overflow-auto flex items-center justify-center p-4">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Skeleton shape="rounded" className="h-full w-full" />
            </div>
          )}
          {error ? (
            <div className="text-center text-red-400">
              <i className="ri-error-warning-line text-4xl mb-2 block"></i>
              <p>{error}</p>
            </div>
          ) : (
            <img
              src={file.url}
              alt={file.name}
              className="max-w-full max-h-full object-contain transition-transform"
              style={{ transform: `scale(${zoom / 100})` }}
              onLoad={handleLoad}
              onError={handleError}
            />
          )}
        </div>
      </div>
    );
  }

  // Other file types - show download option
  return (
    <div className="w-full h-full flex items-center justify-center bg-gray-50">
      <div className="text-center p-8 max-w-md">
        <div className="w-20 h-20 mx-auto mb-4 bg-gray-200 rounded-2xl flex items-center justify-center">
          <i className={`text-4xl ${
            ext === '.doc' || ext === '.docx' ? 'ri-file-word-line text-blue-600' :
            ext === '.xls' || ext === '.xlsx' ? 'ri-file-excel-line text-green-600' :
            ext === '.ppt' || ext === '.pptx' ? 'ri-file-ppt-line text-orange-600' :
            ext === '.zip' || ext === '.rar' ? 'ri-file-zip-line text-yellow-600' :
            'ri-file-line text-gray-400'
          }`}></i>
        </div>

        <h3 className="text-lg font-medium text-gray-900 mb-2">{file.name}</h3>
        <p className="text-sm text-gray-500 mb-6">
          This file type cannot be previewed in the browser.
          Download the file to view it.
        </p>

        <div className="flex items-center justify-center gap-3">
          <a
            href={file.url}
            download={file.name}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
          >
            <i className="ri-download-line"></i>
            Download
          </a>
          <a
            href={file.url}
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <i className="ri-external-link-line"></i>
            Open
          </a>
        </div>

        {file.size && (
          <p className="text-xs text-gray-400 mt-4">
            File size: {formatFileSize(file.size)}
          </p>
        )}
      </div>
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}
